import Typography from "@mui/material/Typography";
import { LegalDocument } from "../components/LegalDocument";
import {
  ACCOUNT_DELETION_RETENTION_DISCLOSURE,
  TRANSACTION_RECORD_RETENTION_DISCLOSURE,
  USAGE_LOG_RETENTION_DISCLOSURE,
  OPTIONAL_PHONE_ACCOUNT_DATA_DISCLOSURE,
  OPTIONAL_TRANSACTIONAL_SMS_DISCLOSURE,
  PLATFORM_LEGAL_DISPLAY_NAME,
  PUBLIC_LEGAL_DRAFT_UPDATED,
} from "@bw/shared";

export function PrivacyPolicyPage() {
  return (
    <LegalDocument title="Privacy policy" updated={PUBLIC_LEGAL_DRAFT_UPDATED}>
      <p>
        {PLATFORM_LEGAL_DISPLAY_NAME}
        (&quot;we,&quot; &quot;us,&quot; or
        &quot;our&quot;) is committed to protecting your privacy. This Privacy
        Policy explains how we collect, use, share, and protect your personal
        information when you use the Badminton Warriors platform, website, and
        mobile applications (collectively, the &quot;Platform&quot;).
      </p>

      <h2>1. Information We Collect</h2>
      <h3>Information you provide</h3>
      <ul>
        <li>
          <strong>Account information:</strong>{" "}
          {OPTIONAL_PHONE_ACCOUNT_DATA_DISCLOSURE}
        </li>
        <li>
          <strong>Booking data:</strong> court reservations, facility
          preferences, and scheduling history
        </li>
        <li>
          <strong>Payment information:</strong> payment method details
          (processed and stored by Stripe; we do not store your full card
          number)
        </li>
        <li>
          <strong>Profile data:</strong> skill level, playing preferences, and
          community memberships
        </li>
        <li>
          <strong>Communications:</strong> messages you send through the
          Platform, support requests, and feedback
        </li>
      </ul>

      <h3>Information collected automatically</h3>
      <ul>
        <li>
          <strong>Usage data:</strong> pages visited, features used, and
          interactions with the Platform
        </li>
        <li>
          <strong>Device information:</strong> device type, operating system,
          browser type, and unique device identifiers
        </li>
        <li>
          <strong>Log data:</strong> IP address, access times, and referring
          URLs
        </li>
      </ul>

      <h2>2. How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul>
        <li>Provide, operate, and maintain the Platform</li>
        <li>
          Process bookings, payments, and communicate with facility operators on
          your behalf
        </li>
        <li>Calculate and maintain player rankings and match history</li>
        <li>
          Send service-related communications (booking confirmations, schedule
          changes, account notifications)
        </li>
        <li>Improve the Platform through usage analytics and feedback</li>
        <li>Detect and prevent fraud, abuse, and security incidents</li>
        <li>Comply with legal obligations</li>
      </ul>
      <p>
        We do not sell your personal information. We do not use your data for
        targeted advertising.
      </p>

      <h3>Optional transactional text messages</h3>
      <p>{OPTIONAL_TRANSACTIONAL_SMS_DISCLOSURE}</p>

      <h2>3. How We Share Your Information</h2>
      <p>We share your information only in the following circumstances:</p>
      <ul>
        <li>
          <strong>Facility operators:</strong> when you make a booking, we share
          your name, contact information, and booking details with the facility
          to fulfill your reservation
        </li>
        <li>
          <strong>Stripe:</strong> payment information is processed by Stripe,
          Inc. under their{" "}
          <a
            href="https://stripe.com/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </li>
        <li>
          <strong>Firebase (Google):</strong> we use Firebase for authentication
          services. Your authentication data is processed under Google&apos;s{" "}
          <a
            href="https://firebase.google.com/support/privacy"
            target="_blank"
            rel="noopener noreferrer"
          >
            Privacy Policy
          </a>
        </li>
        <li>
          <strong>Other users:</strong> your public profile information (display
          name, skill level, community memberships) is visible to other Platform
          users
        </li>
        <li>
          <strong>Legal requirements:</strong> we may disclose information when
          required by law, subpoena, court order, or to protect our rights,
          safety, or the safety of others
        </li>
      </ul>

      <h2>4. Data Retention</h2>
      <p>
        We retain your personal information for as long as your account is
        active or as needed to provide you services. Specifically:
      </p>
      <ul>
        <li>
          <strong>Account and phone-contact data:</strong>{" "}
          {ACCOUNT_DELETION_RETENTION_DISCLOSURE}
        </li>
        <li>
          <strong>Booking and transaction records:</strong>{" "}
          {TRANSACTION_RECORD_RETENTION_DISCLOSURE}
        </li>
        <li>
          <strong>Usage logs:</strong>{" "}
          {USAGE_LOG_RETENTION_DISCLOSURE}
        </li>
        <li>
          <strong>Community content:</strong> retained until you delete it or
          your account is terminated
        </li>
      </ul>

      <h2>5. Your Rights</h2>
      <p>You have the following rights regarding your personal information:</p>
      <ul>
        <li>
          <strong>Access:</strong> request a copy of the personal data we hold
          about you
        </li>
        <li>
          <strong>Correction:</strong> update or correct inaccurate information
          through your account settings or by contacting us
        </li>
        <li>
          <strong>Deletion:</strong> request deletion of your account and
          associated personal data (subject to legal retention requirements)
        </li>
        <li>
          <strong>Data portability:</strong> request an export of your data in a
          machine-readable format
        </li>
        <li>
          <strong>Opt-out:</strong> unsubscribe from non-essential
          communications at any time through your notification preferences
        </li>
      </ul>
      <p>
        To exercise any of these rights, contact us at{" "}
        <a href="mailto:privacy@badmintonwarriors.com">
          privacy@badmintonwarriors.com
        </a>
        . We will respond to your request within 30 days.
      </p>

      <h2>6. Data Security</h2>
      <p>
        We implement appropriate technical and organizational measures to
        protect your personal information, including:
      </p>
      <ul>
        <li>Encryption of data in transit (TLS/HTTPS)</li>
        <li>Encryption of sensitive data at rest</li>
        <li>HttpOnly, secure session cookies</li>
        <li>Regular security assessments</li>
        <li>
          Role-based access controls limiting employee access to personal data
        </li>
      </ul>
      <p>
        No method of transmission or storage is 100% secure. While we strive to
        protect your data, we cannot guarantee absolute security.
      </p>

      <h2>7. Cookies and Tracking</h2>
      <p>
        We use essential cookies for authentication and session management.
        These cookies are strictly necessary for the Platform to function and
        cannot be disabled. We do not use third-party tracking cookies or
        advertising cookies.
      </p>

      <h2>8. Children&apos;s Privacy</h2>
      <p>
        Accounts for children under 13 remain restricted until we record
        verified parental consent. We use the child and guardian information
        needed to request, verify, audit, and revoke that consent and to provide
        permitted features. A parent or guardian may ask to review or delete
        eligible child information or revoke consent by contacting{" "}
        <a href="mailto:privacy@badmintonwarriors.com">
          privacy@badmintonwarriors.com
        </a>. Some safety, consent, transaction, or legal records may remain
        retained or de-identified as described in the applicable retention
        policy and exact accepted notices.
      </p>

      <h2>9. California Privacy Rights (CCPA)</h2>
      <p>
        If you are a California resident, you have additional rights under the
        California Consumer Privacy Act (CCPA):
      </p>
      <ul>
        <li>
          <strong>Right to know:</strong> you may request details about the
          categories and specific pieces of personal information we have
          collected about you in the past 12 months
        </li>
        <li>
          <strong>Right to delete:</strong> you may request deletion of your
          personal information, subject to certain exceptions
        </li>
        <li>
          <strong>Right to non-discrimination:</strong> we will not discriminate
          against you for exercising your CCPA rights
        </li>
        <li>
          <strong>No sale of personal information:</strong> we do not sell your
          personal information and have not done so in the preceding 12 months
        </li>
      </ul>
      <p>
        To submit a CCPA request, email{" "}
        <a href="mailto:privacy@badmintonwarriors.com">
          privacy@badmintonwarriors.com
        </a>{" "}
        with the subject line &quot;CCPA Request.&quot; We will verify your
        identity before processing your request.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. When we make
        material changes, we will notify you by email or through a prominent
        notice on the Platform at least 30 days before the changes take effect.
        Your continued use of the Platform after the effective date constitutes
        your acceptance of the updated policy.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have questions or concerns about this Privacy Policy or our data
        practices, please contact us at:
      </p>
      <p>
        {PLATFORM_LEGAL_DISPLAY_NAME}
        <br />
        Privacy Team
        <br />
        Email:{" "}
        <a href="mailto:privacy@badmintonwarriors.com">
          privacy@badmintonwarriors.com
        </a>
      </p>
    </LegalDocument>
  );
}
